import { GetState, SetState } from 'zustand';
import {
  EmailEffectivenessValues,
  effectivenessKeys,
} from '@groove/api/v2/emailEffectiveness';
import { ReactElement } from 'react';

import {
  COMPOSE_PANE_IFRAME_NAME,
  COMPOSE_PANE_IFRAME_NATIVE_NAME,
} from '../constants';
import { createDisplayComponents } from '../SideBar/createDisplayComponents';

import useStore, { Store } from './useStore';

// The following code is needed for window.frames otherwise it expects an integer opposed to a string
declare global {
  interface Window {
    GLOBALS: unknown[];
    [index: string]: Window;
  }
}

export type ActionsSidePaneState = 'EFFECTIVENESS' | 'INSIGHTS' | false;

type EmailEffectivenessParams = Partial<
  Omit<EmailEffectivenessValues, 'summary'>
>;

type EffectivenessValue = {
  displayPercentage: number;
  displayComponent: ReactElement | null;
};

type EmailEffectivenessResult = {
  [key in effectivenessKeys]: EffectivenessValue;
};

type CalcInterpolationParam = {
  x: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
};

type CalcEmailEffectivenessValues = {
  rawValues: EmailEffectivenessParams;
  storeValues: EmailEffectivenessResult;
};

const calcInterpolation = ({
  x,
  x0,
  x1,
  y0,
  y1,
}: CalcInterpolationParam): number => {
  const a = (y1 - y0) / (x1 - x0);
  const b = -a * x0 + y0;
  return a * x + b;
};

const getLutValues = ({
  key,
  value,
}: {
  key: `${effectivenessKeys}`;
  value?: number;
}): EffectivenessValue => {
  const lut = createDisplayComponents[key];
  let displayComponent: ReactElement | null = null;
  let displayPercentage = 0;
  if (typeof value !== 'undefined' && lut) {
    for (let i = 0; i < lut.length; i += 1) {
      if (value <= lut[i].upperValue) {
        displayComponent = lut[i].displayComponent(value);
        if (i === 0) {
          displayPercentage = lut[i].displayPercentage;
        } else {
          const prevValue = lut[i - 1];
          const currentValue = lut[i];
          displayPercentage = calcInterpolation({
            x: value,
            x0: prevValue.upperValue,
            x1: currentValue.upperValue,
            y0: prevValue.displayPercentage,
            y1: currentValue.displayPercentage,
          });
        }
        break;
      }
      if (value > lut[i].upperValue && i === lut.length - 1) {
        displayComponent = lut[i].displayComponent(value);
        displayPercentage = lut[i].displayPercentage;
        break;
      }
    }
  }
  return {
    displayPercentage,
    displayComponent,
  };
};

const calcEmailEffectivenessValues = ({
  rawValues,
  storeValues,
}: CalcEmailEffectivenessValues): EmailEffectivenessResult => {
  const calcValues: Partial<EmailEffectivenessResult> = {};
  let summaryTotal = 0;

  for (const [rawKey, rawValue] of Object.entries(rawValues)) {
    const key = rawKey as Exclude<`${effectivenessKeys}`, 'summary'>;

    // We are going to interpolate the data using the imported createDisplayComponents
    calcValues[key] = getLutValues({ key, value: rawValue });
  }

  const resultValues = { ...storeValues, ...calcValues };

  // Next 2 sections is for calculating the summary
  const resultKeys = Object.keys(resultValues).filter(rawValue => {
    const value = rawValue as `${effectivenessKeys}`;
    return value !== 'summary' && resultValues[value].displayPercentage !== 0;
  }) as Exclude<`${effectivenessKeys}`, 'summary'>[];

  resultKeys.forEach(
    key => (summaryTotal += resultValues[key].displayPercentage),
  );

  resultValues.summary = getLutValues({
    key: 'summary',
    value: summaryTotal / resultKeys.length || 0,
  });

  return resultValues;
};

const postEmailEffectivenessToFEBES = (percentage: number): void => {
  const febesUrl = useStore.getState().actionComposeValues.febesURL;
  const origin = febesUrl || process.env.NX_FEBES_URL || '';
  let trimmedOrigin = '';
  // Needed for review apps
  try {
    const originUrl = new URL(origin);
    trimmedOrigin = originUrl.origin;
  } catch {
    trimmedOrigin = origin;
  }
  const payload = {
    type: 'ACTION_COMPOSE/SET_EMAIL_EFFECTIVENESS_VALUE',
    payload: {
      value: percentage,
    },
  };
  if (window.location.href.includes(`${origin}/actions`)) {
    window.frames[COMPOSE_PANE_IFRAME_NATIVE_NAME]?.postMessage(
      payload,
      trimmedOrigin,
    );
  } else {
    window.frames[COMPOSE_PANE_IFRAME_NAME]?.postMessage(
      payload,
      trimmedOrigin,
    );
  }
};

export type SidePaneStore = {
  emailEffectivenessValues: EmailEffectivenessResult;
  updateEmailEffectivenessValues: (values: EmailEffectivenessParams) => void;
  clearEmailEffectivenessValues: () => void;
  emailBody: string;
  setEmailBody: (emailBody: string) => void;
  actionsSidePaneState: ActionsSidePaneState;
  setActionsSidePaneState: (actionsSidePaneState: ActionsSidePaneState) => void;
  isEmailPreviewOpen: boolean;
  setIsEmailPreviewOpen: (isEmailPreviewOpen: boolean) => void;
};

const emailEffectivenessValues: EmailEffectivenessResult = {
  emailLength: {
    displayPercentage: 0,
    displayComponent:
      createDisplayComponents.emailLength[0].displayComponent(0),
  },
  subjectLength: {
    displayPercentage: 0,
    displayComponent:
      createDisplayComponents.subjectLength[0].displayComponent(0),
  },
  positivity: {
    displayPercentage: 0,
    displayComponent: createDisplayComponents.positivity[0].displayComponent(0),
  },
  gradeLevel: {
    displayPercentage: 0,
    displayComponent: createDisplayComponents.gradeLevel[0].displayComponent(0),
  },
  summary: {
    displayPercentage: 0,
    displayComponent: createDisplayComponents.summary[0].displayComponent(0),
  },
};

export const sideBarStore = (
  set: SetState<Store>,
  get: GetState<Store>,
): SidePaneStore => ({
  emailEffectivenessValues,
  updateEmailEffectivenessValues: rawValues => {
    const { emailEffectivenessValues: storeValues } = get();
    const emailEffectivenessValues = calcEmailEffectivenessValues({
      storeValues,
      rawValues,
    });
    postEmailEffectivenessToFEBES(
      emailEffectivenessValues.summary.displayPercentage,
    );
    set({ emailEffectivenessValues });
  },
  clearEmailEffectivenessValues: () => {
    postEmailEffectivenessToFEBES(0);
    set({ emailEffectivenessValues, emailBody: '' });
  },
  emailBody: '',
  setEmailBody: emailBody => set({ emailBody }),
  actionsSidePaneState: false,
  setActionsSidePaneState: actionsSidePaneState =>
    set({ actionsSidePaneState }),
  isEmailPreviewOpen: false,
  setIsEmailPreviewOpen: isEmailPreviewOpen => set({ isEmailPreviewOpen }),
});
